import { ReactNode, useContext, useMemo } from "react"
import UserContext from "../contexts/UserContext/context"
import { ICON } from "../components/Icon/data"
import AreaMonitoring from "../pages/AreaMonitoring"
import Maintenance from "../pages/Maintenance"
import { useTranslation } from "react-i18next"
import Dashboard from "../pages/Dashboard"
import LiveMap from "../pages/LiveMap"
import Reports from "../pages/Reports"
import { useReports } from "./useReports"
import ProfileSettings from "../pages/ProfileSettings"
import Management from "../pages/Management"
import { useUser } from "./useUser"
import History from "../pages/History"

export type Path = {
  label: string,
  url: string,
  elem: ReactNode,
  icon?: string,
  active_icon?: string,
  permissions: boolean,
  inMenu?: boolean,
  params?: string,
  preference_key?: string,
}

export const usePaths = () => {
  const { t } = useTranslation()
  const { user } = useContext(UserContext)
  const { permissions } = user

  const { isAdmin } = useUser()
  const { showReports } = useReports()

  const availablePaths: Record<string, Path> = useMemo(() => ({
    DASHBOARD: {
      label: t('Dashboard'),
      url: '/dashboard',
      icon: ICON.METER,
      active_icon: ICON.METER_FILLED,
      elem: <Dashboard />,
      permissions: !permissions?.trackwell?.cs_dashboard,
      inMenu: true,
      preference_key: 'fleet-dashboard'
    },
    LIVE_MAP: {
      label: t('Live Map'),
      url: '/live-map',
      icon: ICON.GLOBE_NETWORK,
      active_icon: ICON.GLOBE_NETWORK_FILLED,
      elem: <LiveMap />,
      permissions: !permissions?.trackwell?.cs_livemap,
      inMenu: true,
      preference_key: 'fleet-map'
    },
    REPORTS: {
      label: t('Reports'),
      url: '/reports',
      params: ':type?',
      icon: ICON.METER,
      active_icon: ICON.METER_FILLED,
      elem: <Reports />,
      permissions: showReports,
      inMenu: true,
    },
    PROFILE_SETTINGS: {
      label: t('Profile settings'),
      url: '/profile-settings',
      elem: <ProfileSettings />,
      permissions: !permissions?.trackwell?.user_profile
    },
    MANAGEMENT: {
      label: t('Manage'),
      url: '/manage',
      params: ':view?',
      elem: <Management />,
      permissions: isAdmin
    },
    HISTORY: {
      label: t('History'),
      url: '/history',
      icon: ICON.VEHICLE_INSIGHT,
      active_icon: ICON.VEHICLE_INSIGHT_FILLED,
      elem: <History />,
      permissions: !permissions?.trackwell?.cs_history,
      inMenu: true,
      preference_key: 'fleet-history-map'
    },
    AREA_MONITORING: {
      label: t('Area monitoring'),
      url: '/area-monitoring',
      icon: ICON.ZOOM_AREA,
      active_icon: ICON.ZOOM_AREA_FILLED,
      elem: <AreaMonitoring />,
      permissions: !permissions?.trackwell?.cs_report_areamonitoring,
      inMenu: true,
    },
    MAINTENANCE: {
      label: t('Maintenance'),
      url: '/maintenance',
      icon: ICON.TOOL_KIT,
      active_icon: ICON.TOOL_KIT_FILLED,
      elem: <Maintenance />,
      permissions: !permissions?.trackwell?.cs_maintenance,
      inMenu: true
    }
  }), [t, permissions, showReports, isAdmin])

  const authorizedPaths = useMemo(() => {
    return Object.keys(availablePaths).reduce((obj: Record<string, Path>, key) => {
      const path = availablePaths[key]
      if (path.permissions) obj[key] = path
      return obj
    }, {});
  }, [permissions, availablePaths])

  const preferencePath = useMemo(() => {
    const prefPath = user?.preferences?.DEFAULT_LANDING_SITE ?? 'fleet-map'
    const foundPath = Object.keys(availablePaths).find((p) => availablePaths[p].preference_key === prefPath)
    return availablePaths[foundPath]?.url
  }, [user.preferences, availablePaths])

  return {
    paths: authorizedPaths,
    preferencePath
  }
}
